import { z } from "zod";
import { TovActorTypeSchema } from "../../enums/tov";
import {
  TovBoundaryTypeSchema,
  TovExampleTypeSchema,
  TovLexiconTypeSchema,
  TovRuleSeveritySchema,
  TovRuleTypeSchema,
  TovScopeTypeSchema,
  TovVisualRuleTypeSchema,
} from "../../enums/tov-voice";
import { TovDefinitionEvidenceResponseSchema } from "./evidence.responses";

// Wire shapes for the manual TOV definition tables. Nullable DB columns surface
// as nullable here; the store renders explicit NULL. Timestamps are ISO-8601.
const linkedEvidence = z.array(TovDefinitionEvidenceResponseSchema);

export const TovTraitBoundaryResponseSchema = z.object({
  id: z.uuid(),
  traitId: z.uuid(),
  boundaryType: TovBoundaryTypeSchema,
  text: z.string(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovTraitBoundaryResponse = z.infer<
  typeof TovTraitBoundaryResponseSchema
>;

export const TovTraitResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  targetStrength: z.number().int(),
  displayOrder: z.number().int(),
  boundaries: z.array(TovTraitBoundaryResponseSchema),
  linkedEvidence,
});
export type TovTraitResponse = z.infer<typeof TovTraitResponseSchema>;

export const TovToneDimensionResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  key: z.string(),
  label: z.string(),
  lowLabel: z.string(),
  highLabel: z.string(),
  targetMin: z.number().int(),
  targetMax: z.number().int(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovToneDimensionResponse = z.infer<
  typeof TovToneDimensionResponseSchema
>;

export const TovRuleResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  ruleType: TovRuleTypeSchema,
  severity: TovRuleSeveritySchema,
  scopeType: TovScopeTypeSchema,
  scopeKey: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  rationale: z.string().nullable(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovRuleResponse = z.infer<typeof TovRuleResponseSchema>;

export const TovLexiconResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  term: z.string(),
  lexiconType: TovLexiconTypeSchema,
  replacement: z.string().nullable(),
  locale: z.string().nullable(),
  scopeType: TovScopeTypeSchema,
  scopeKey: z.string().nullable(),
  reason: z.string().nullable(),
  linkedEvidence,
});
export type TovLexiconResponse = z.infer<typeof TovLexiconResponseSchema>;

export const TovExampleResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  exampleType: TovExampleTypeSchema,
  scopeType: TovScopeTypeSchema,
  scopeKey: z.string().nullable(),
  inputText: z.string().nullable(),
  outputText: z.string(),
  explanation: z.string(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovExampleResponse = z.infer<typeof TovExampleResponseSchema>;

export const TovAudienceResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  needs: z.string(),
  painPoints: z.string(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovAudienceResponse = z.infer<typeof TovAudienceResponseSchema>;

export const TovContentPillarResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  objective: z.string(),
  weight: z.number().int().nullable(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovContentPillarResponse = z.infer<
  typeof TovContentPillarResponseSchema
>;

export const TovJourneyStageResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovJourneyStageResponse = z.infer<
  typeof TovJourneyStageResponseSchema
>;

export const TovVisualGuidelineResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  scopeType: TovScopeTypeSchema,
  scopeKey: z.string().nullable(),
  visualRuleType: TovVisualRuleTypeSchema,
  title: z.string(),
  description: z.string(),
  displayOrder: z.number().int(),
  linkedEvidence,
});
export type TovVisualGuidelineResponse = z.infer<
  typeof TovVisualGuidelineResponseSchema
>;

export const TovRevisionEventResponseSchema = z.object({
  id: z.uuid(),
  versionId: z.uuid(),
  actorType: TovActorTypeSchema,
  eventType: z.string(),
  targetTable: z.string(),
  targetId: z.uuid().nullable(),
  summary: z.string(),
  createdAt: z.iso.datetime(),
});
export type TovRevisionEventResponse = z.infer<
  typeof TovRevisionEventResponseSchema
>;
